import { forwardRef, useId } from 'react';
import { cn } from '../../lib/format.js';

/**
 * Admin form controls. Same contract as the storefront's — labelled, described,
 * errors announced — at operations density.
 */

const control =
  'w-full rounded border bg-surface px-2.5 text-xs text-ink placeholder:text-muted/60 ' +
  'transition-colors duration-100 focus:outline-2 focus:outline-offset-1 ' +
  'disabled:cursor-not-allowed disabled:bg-paper disabled:text-muted';

const state = (invalid) =>
  invalid ? 'border-danger focus:outline-danger' : 'border-mist hover:border-muted/60 focus:outline-signal';

export const Label = ({ htmlFor, required, hint, children }) => (
  <div className="mb-1 flex items-baseline justify-between gap-2">
    <label htmlFor={htmlFor} className="text-2xs font-medium text-ink">
      {children}
      {required && <span className="ml-0.5 text-muted" aria-hidden="true">*</span>}
    </label>
    {hint && <span className="text-2xs text-muted">{hint}</span>}
  </div>
);

export const FieldError = ({ id, children }) =>
  children ? (
    <p id={id} role="alert" className="mt-1 text-2xs text-danger">
      {children}
    </p>
  ) : null;

export const Input = forwardRef(function Input(
  { label, error, hint, help, required, prefix, className, id: providedId, ...props },
  ref,
) {
  const generated = useId();
  const id = providedId ?? generated;

  return (
    <div className={className}>
      {label && <Label htmlFor={id} required={required} hint={hint}>{label}</Label>}

      <div className="relative">
        {prefix && (
          <span className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 font-mono text-2xs text-muted">
            {prefix}
          </span>
        )}
        <input
          ref={ref}
          id={id}
          required={required}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={error ? `${id}-error` : undefined}
          className={cn(control, state(error), 'h-8', prefix && 'pl-7')}
          {...props}
        />
      </div>

      {help && !error && <p className="mt-1 text-2xs text-muted">{help}</p>}
      <FieldError id={`${id}-error`}>{error}</FieldError>
    </div>
  );
});

export const Textarea = forwardRef(function Textarea(
  { label, error, hint, help, required, rows = 3, className, id: providedId, ...props },
  ref,
) {
  const generated = useId();
  const id = providedId ?? generated;

  return (
    <div className={className}>
      {label && <Label htmlFor={id} required={required} hint={hint}>{label}</Label>}
      <textarea
        ref={ref}
        id={id}
        rows={rows}
        required={required}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={error ? `${id}-error` : undefined}
        className={cn(control, state(error), 'resize-y py-2 leading-relaxed')}
        {...props}
      />
      {help && !error && <p className="mt-1 text-2xs text-muted">{help}</p>}
      <FieldError id={`${id}-error`}>{error}</FieldError>
    </div>
  );
});

export const Select = forwardRef(function Select(
  { label, error, hint, required, options = [], placeholder, className, id: providedId, children, ...props },
  ref,
) {
  const generated = useId();
  const id = providedId ?? generated;

  return (
    <div className={className}>
      {label && <Label htmlFor={id} required={required} hint={hint}>{label}</Label>}
      <div className="relative">
        <select
          ref={ref}
          id={id}
          required={required}
          aria-invalid={error ? 'true' : undefined}
          className={cn(control, state(error), 'h-8 appearance-none pr-7')}
          {...props}
        >
          {placeholder && <option value="">{placeholder}</option>}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
          {children}
        </select>
        <svg
          viewBox="0 0 16 16"
          aria-hidden="true"
          className="pointer-events-none absolute top-1/2 right-2 size-3 -translate-y-1/2 text-muted"
          fill="currentColor"
        >
          <path d="M4.2 6.2a.75.75 0 0 1 1.06 0L8 8.94l2.74-2.74a.75.75 0 1 1 1.06 1.06L8.53 10.53a.75.75 0 0 1-1.06 0L4.2 7.26a.75.75 0 0 1 0-1.06Z" />
        </svg>
      </div>
      <FieldError id={`${id}-error`}>{error}</FieldError>
    </div>
  );
});

export const Checkbox = forwardRef(function Checkbox(
  { label, description, className, id: providedId, ...props },
  ref,
) {
  const generated = useId();
  const id = providedId ?? generated;

  return (
    <div className={cn('flex items-start gap-2', className)}>
      <input
        ref={ref}
        id={id}
        type="checkbox"
        className={cn(
          'mt-px size-3.5 shrink-0 cursor-pointer appearance-none rounded-[3px] border border-mist bg-surface',
          'checked:border-ink checked:bg-ink',
          'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-signal',
          'bg-[length:10px] bg-center bg-no-repeat',
          "checked:bg-[url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23fff'%3E%3Cpath d='M6.2 11.5 3 8.3l1.06-1.06L6.2 9.4l5.74-5.74L13 4.72z'/%3E%3C/svg%3E\")]",
        )}
        {...props}
      />
      {label && (
        <div className="min-w-0">
          <label htmlFor={id} className="cursor-pointer text-2xs text-ink">
            {label}
          </label>
          {description && <p className="mt-0.5 text-2xs text-muted">{description}</p>}
        </div>
      )}
    </div>
  );
});

/** A labelled switch for settings, where on/off reads better than a tick. */
export const Toggle = forwardRef(function Toggle(
  { label, description, checked, onChange, disabled, className, id: providedId, ...props },
  ref,
) {
  const generated = useId();
  const id = providedId ?? generated;

  // Rendered without a label the text column is dead weight — it still claimed a
  // gap beside the switch and left an empty <label> as the control's only name.
  // Callers in that shape pass their own aria-label.
  const labelled = Boolean(label || description);

  return (
    <div className={cn('flex items-start gap-4', labelled && 'justify-between', className)}>
      {labelled && (
        <div className="min-w-0">
          {label && (
            <label htmlFor={id} className="cursor-pointer text-xs text-ink">
              {label}
            </label>
          )}
          {description && <p className="mt-0.5 text-2xs leading-relaxed text-muted">{description}</p>}
        </div>
      )}

      <button
        ref={ref}
        id={id}
        type="button"
        role="switch"
        disabled={disabled}
        aria-checked={Boolean(checked)}
        onClick={() => onChange?.(!checked)}
        className={cn(
          'relative h-5 w-9 shrink-0 rounded-full transition-colors',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-signal',
          'disabled:cursor-not-allowed disabled:opacity-50',
          checked ? 'bg-ink' : 'bg-mist',
        )}
        {...props}
      >
        {/*
          left is pinned rather than left to the static position. A <button> is
          text-align:center in the UA sheet and preflight does not reset it, so a
          knob with only a top offset starts centred — (36-16)/2 = 10px — and the
          travel was being added to that. Turning the switch on put it at 28px in
          a 36px track and 8px of it hung outside. Inset 2px each side, so the
          travel is 36 - 16 - 2 - 2 = 16px and both ends sit symmetrically.
        */}
        <span
          aria-hidden="true"
          className={cn(
            'absolute top-0.5 left-0.5 size-4 rounded-full bg-white transition-transform',
            checked ? 'translate-x-4' : 'translate-x-0',
          )}
        />
      </button>
    </div>
  );
});
